import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { fetchMyConversation, fetchConversationMessages, sendConversationMessage } from "../api/api";
import { avatarColor, avatarInitials, formatActivityDateLabel, formatActivityTime } from "../utils/activityAvatar";

const PAGE_SIZE = 5;

function Messages() {
  const { user } = useAuth();
  const { refreshUnreadCount } = useNotifications();

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]); // newest-first, as returned by the API
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const conv = await fetchMyConversation();
        setConversationId(conv.id);
        const page = await fetchConversationMessages(conv.id, 0, PAGE_SIZE);
        setMessages(page);
        setHasMore(page.length === PAGE_SIZE);
        // Reading the thread marks messages read server-side (same as the
        // mobile app) — refresh the header bell immediately instead of
        // waiting for its next poll.
        refreshUnreadCount();
      } catch (err) {
        setError(err.response?.data?.detail || "Could not load your messages. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [refreshUnreadCount]);

  async function handleLoadMore() {
    if (!conversationId) return;
    setLoadingMore(true);
    try {
      const page = await fetchConversationMessages(conversationId, messages.length, PAGE_SIZE);
      setMessages((prev) => [...prev, ...page]);
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load more messages.");
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!draft.trim() || !conversationId) return;
    setSending(true);
    try {
      const msg = await sendConversationMessage(conversationId, draft.trim());
      setMessages((prev) => [msg, ...prev]);
      setDraft("");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send your message.");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">
        <div className="messages-page">

          <div className="messages-header">
            <h2>Messages</h2>
            <div className="message-count">{messages.length}</div>
          </div>

          {loading && <p>Loading your messages…</p>}
          {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

          {!loading && !error && (
            <>
              <form className="message-card" onSubmit={handleSend} style={{ marginBottom: "1rem" }}>
                <textarea
                  rows="3"
                  placeholder="Write a message to Jain Farms…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  style={{ width: "100%" }}
                />
                <div style={{ marginTop: "0.5rem" }}>
                  <button type="submit" className="send-btn" disabled={sending || !draft.trim()}>
                    {sending ? "Sending…" : "Send"}
                  </button>
                </div>
              </form>

              {messages.length === 0 && <p>No messages yet.</p>}

              {messages.map((msg, i) => {
                const fromAdmin = msg.sender_id !== user?.id;
                const sender = fromAdmin ? "Jain Farms" : "You";
                const dateLabel = formatActivityDateLabel(msg.created_at);
                const prevDateLabel = i > 0 ? formatActivityDateLabel(messages[i - 1].created_at) : null;
                return (
                  <React.Fragment key={msg.id}>
                    {dateLabel !== prevDateLabel && <div className="activity-date-header">{dateLabel}</div>}
                    <div className="activity-row">
                      <div className="activity-avatar" style={{ background: avatarColor(fromAdmin) }}>
                        {avatarInitials(sender)}
                      </div>
                      <div className="activity-body-col">
                        <div className="activity-meta">
                          {formatActivityTime(msg.created_at)} <span className="activity-sender">{sender}</span>
                          {fromAdmin && !msg.is_read && <span className="new-tag" style={{ marginLeft: "8px" }}>New</span>}
                        </div>
                        <p className="activity-text">{msg.body}</p>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {hasMore && (
                <button className="send-btn" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </>
          )}

        </div>
      </div>
    </>
  );
}

export default Messages;
